import React, { useEffect, useState } from 'react';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


interface TurnosSignUpSelectionProps {
    jugador: any;
    tournament: any;
    discountCode?: string;
    division: any;
    paquete: any;
    onSelectionComplete: (data: any) => void;
    onRemove: () => void;
    usedTurnos?: number[];
    onAddExtraPackageClick?: () => void;
    onAddSideEventClick?: () => void;
    index?: number;
}

const TurnosSignUpSelection: React.FC<TurnosSignUpSelectionProps> = ({
    jugador,
    tournament,
    discountCode,
    division,
    paquete,
    onSelectionComplete,
    onRemove,
    usedTurnos,
    onAddExtraPackageClick,
    onAddSideEventClick,
    index,
}) => {
    const [descripcion, setDescripcion] = useState<{ eventos: any[]; items: any[] }>({ eventos: [], items: [] });
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [turnos, setTurnos] = useState<any[]>([]);
    const [selectedTurnos, setSelectedTurnos] = useState<{ [eventId: string]: Set<number> }>({});
    const selectedTurnoIds = Object.values(selectedTurnos).flatMap(set => Array.from(set));
    const [waitlistTurnos, setWaitlistTurnos] = useState<Set<number>>(new Set());
    const hasTurnoConflict = selectedTurnoIds.some(id => (usedTurnos?.includes(id)));
    const [allTurnos, setAllTurnos] = useState<{ [eventId: string]: any[] }>({});
    const [turnosInscrito, setTurnosInscrito] = useState<number[]>([]);
	const [isAddedToCheckout, setIsAddedToCheckout] = useState(false);


    // Fetch main package description
    useEffect(() => {
        if (tournament?.clave && paquete?.CLAVE) {
            fetch(
                `${BACKEND_URL}/api/paquetes/descripcion/${tournament.clave}/${paquete.CLAVE}`
            )
                .then(res => res.json())
                .then(data => {
                    setDescripcion(data);
                    if (data.eventos && data.eventos.length > 0) {
                        setSelectedEvent(data.eventos[0]);
                    }
                });
        }
    }, [tournament, paquete]);


    // Fetch turnos for main package selected event
    useEffect(() => {
        if (selectedEvent && tournament?.clave) {
            fetch(
                `${BACKEND_URL}/api/turnos/${selectedEvent.CVEEVENTO}/${tournament.clave}/${division.CLAVE}`
            )
                .then(res => res.json())
                .then(data => {
                    setTurnos(data);
                    setAllTurnos(prev => ({
                        ...prev,
                        [selectedEvent.CVEEVENTO]: data,
                    }));
                });
        }
    }, [selectedEvent, tournament, division]);


    // Fetch turnosInscrito
    useEffect(() => {
        if (tournament?.clave && jugador?.CLAVE) {
            fetch(
                `${BACKEND_URL}/api/turnos/jug/inscrito/${tournament.clave}/${jugador.CLAVE}`
            )
                .then(res => res.json())
                .then(data => {
                    if (data.turnosInscrito) {
                        setTurnosInscrito(data.turnosInscrito);
                    }
                });
        }
    }, [tournament, jugador]);

    const maxForCurrentEvent = selectedEvent?.CANTIDAD || 0;
    const selectedForCurrentEvent = selectedTurnos[selectedEvent?.CVEEVENTO] || new Set();

    const handleCheckboxChange = (turnoId: number, checked: boolean) => {
        setSelectedTurnos(prev => {
            const eventId = selectedEvent.CVEEVENTO;
            const prevSet = new Set(prev[eventId] || []);
            if (checked) {
                if (prevSet.size < maxForCurrentEvent) {
                    prevSet.add(turnoId);
                }
            } else {
                prevSet.delete(turnoId);
            }
            return { ...prev, [eventId]: prevSet };
        });
    };

    const allEventsSatisfied =
        descripcion.eventos.length === 0 ||
        descripcion.eventos.every(ev => {
            const sel = selectedTurnos[ev.CVEEVENTO];
            return sel && sel.size === ev.CANTIDAD;
        });
    

    return (
        <div className="bg-white shadow-2xl rounded-3xl p-6 sm:p-8 w-full border-l-4 border-indigo-500">
            {/* Header */}
            <div className="mb-6 pb-6 border-b-2 border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-600 rounded-full p-2">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                            Cart Item #{index !== undefined ? index + 1 : ''}
                        </h2>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-sm font-bold shadow-md ${
                        isAddedToCheckout 
                            ? 'bg-green-500 text-white' 
                            : 'bg-amber-400 text-amber-900'
                    }`}>
                        {isAddedToCheckout ? '✓ Ready for Checkout' : '⚠ Complete Selection'}
                    </span>
                </div>
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">PACKAGE</span>
                        <span className="font-bold text-lg text-gray-900">{paquete.DESCRIPCION}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                        <span className="font-semibold">{tournament.nombre}</span>
                    </div>
                </div>
            </div>
            {/* Package Contents */}
            <div className="mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                    Package Includes:
                </h3>
                <div className="space-y-2">
                    {descripcion.eventos.map((ev: any) => (
                        <div key={ev.CVEEVENTO} className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                            <div className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                {ev.CANTIDAD}
                            </div>
                            <span className="font-semibold text-gray-800">{ev.EVENTO}</span>
                        </div>
                    ))}
                    {descripcion.items.map((it: any) => (
                        <div key={it.CVEITEM} className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                            <div className="bg-green-100 text-green-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                {it.CANTIDAD}
                            </div>
                            <span className="font-semibold text-gray-800">{it.ITEM}</span>
                        </div>
                    ))}
                </div>
            </div>
			
			 {/* Informational Banner */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-5 mb-6 shadow-lg text-white">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 bg-white/20 rounded-full p-2">
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div>
                        <h4 className="font-bold text-lg mb-2">💡 Important Information</h4>
                        <p className="text-sm leading-relaxed">
                            If your desired squads are <span className="font-bold">FULL</span>, you can still register by selecting available squads and <span className="font-bold underline">waitlist</span> yourself for the ones you wish to bowl. You'll be notified if spots become available.
                        </p>
                    </div>
                </div>
            </div>

            {/* Event Selection */}
            {descripcion.eventos.length > 0 && (
                <div className="mb-6">
                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 mb-4">
                        <p className="text-sm text-amber-900">
                            <span className="font-bold">📋 Instructions:</span> Select an event tab below and choose your preferred squads. Complete all events to proceed.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {descripcion.eventos.map((ev: any) => {
                            const isSelected = selectedEvent?.CVEEVENTO === ev.CVEEVENTO;
                            const isComplete = selectedTurnos[ev.CVEEVENTO]?.size === ev.CANTIDAD;
                            return (
                                <button
                                    key={ev.CVEEVENTO}
                                    className={`px-5 py-3 rounded-xl font-bold text-sm sm:text-base transition-all duration-200 shadow-md relative ${
                                        isSelected
                                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white scale-105 shadow-lg'
                                            : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
                                    }`}
                                    onClick={() => setSelectedEvent(ev)}
                                >
                                    {ev.EVENTO}
                                    {isComplete && (
                                        <span className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-lg">
                                            ✓
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {selectedEvent && (
                <div className="mt-6">
                    <div className="mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 text-white shadow-lg">
                        <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {selectedEvent.EVENTO}
                        </h3>
                        <p className="text-sm">
                            Select <span className="font-bold text-yellow-300">{selectedEvent.CANTIDAD}</span> squad(s) | 
                            Currently selected: <span className="font-bold text-yellow-300">{selectedForCurrentEvent.size}</span>
                        </p>
                    </div>
                    <div className="grid-container grid grid-cols-1 gap-3 max-h-96 overflow-y-auto pr-2">
                        {turnos
                            .sort((a, b) => a.TURNO - b.TURNO)
                            .map((turno: any) => {
                                const checked = selectedForCurrentEvent.has(turno.CLAVE);
                                const isWaitlist = waitlistTurnos.has(turno.CLAVE);
                                const isFull = turno.DIFERENCIA < 0;
                                const isAlreadyRegistered = turnosInscrito.includes(turno.CLAVE);
                                const disabled = isFull || (!checked && selectedForCurrentEvent.size >= maxForCurrentEvent);

                                return (
                                    <div
                                        key={turno.TURNO}
                                        className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl shadow-md border-2 transition-all duration-200 ${
                                            checked
                                                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-500 shadow-lg'
                                                : isFull
                                                ? 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300'
                                                : isAlreadyRegistered
                                                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300'
                                                : disabled
                                                ? 'bg-gray-100 border-gray-300 opacity-60'
                                                : 'bg-white border-gray-200 hover:border-indigo-400 hover:shadow-lg'
                                        }`}
                                    >
                                        <div className="flex-1 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="bg-indigo-600 text-white rounded-lg px-3 py-1 font-bold text-sm min-w-[80px] text-center">
                                                    Squad {turno.TURNO}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-gray-900">{turno.DIASEMANA_I}</span>
                                                    <span className="text-sm text-gray-600">{turno.HORARIO_I}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {isFull ? (
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                        <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                                            🚫 FULL
                                                        </span>
                                                        <label className="flex items-center gap-2 bg-amber-100 px-3 py-1 rounded-lg border border-amber-300">
                                                            <input
                                                                type="checkbox"
                                                                checked={isWaitlist}
                                                                disabled={isAlreadyRegistered}
                                                                onChange={e => {
                                                                    setWaitlistTurnos(prev => {
                                                                        const newSet = new Set(prev);
                                                                        if (e.target.checked) {
                                                                            newSet.add(turno.CLAVE);
                                                                        } else {
                                                                            newSet.delete(turno.CLAVE);
                                                                        }
                                                                        return newSet;
                                                                    });
                                                                }}
                                                                className="w-5 h-5"
                                                            />
                                                            <span className="text-amber-800 font-bold text-sm">Waitlist</span>
                                                            <span className="bg-amber-500 text-yellow-500 px-2 py-0.5 rounded-full text-xs font-bold">{turno.WLINSCRITOS}</span>
                                                        </label>
                                                    </div>
                                                ) : isAlreadyRegistered ? (
                                                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                                        ✓ REGISTERED
                                                    </span>
                                                ) : disabled ? (
                                                    <span className="bg-gray-400 text-white px-3 py-1 rounded-full text-xs font-bold">
                                                        UNAVAILABLE
                                                    </span>
                                                ) : (
                                                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                                        {turno.DIFERENCIA} SPOTS LEFT
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {!isFull && (
                                            <label className="flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={disabled || isAlreadyRegistered}
                                                    onChange={e => handleCheckboxChange(turno.CLAVE, e.target.checked)}
                                                    className="w-7 h-7 rounded-lg border-2 border-gray-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed"
                                                />
                                            </label>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* Add More */}
            <div className="mt-6 flex flex-wrap gap-3">
                {onAddExtraPackageClick && (
                    <button
                        className="flex items-center gap-2 px-5 py-3 rounded-xl bg-green-600 text-white hover:bg-green-700 font-bold text-sm shadow-lg hover:shadow-xl transition-all"
                        onClick={onAddExtraPackageClick}
                        type="button"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Re-entry
                    </button>
                )}
                {onAddSideEventClick && (
                    <button
                        className="flex items-center gap-2 px-5 py-3 rounded-xl bg-yellow-600 text-white hover:bg-yellow-700 font-bold text-sm shadow-lg hover:shadow-xl transition-all"
                        onClick={onAddSideEventClick}
                        type="button"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Side Event
                    </button>
                )}
            </div>

            {/* Action Buttons */}
            <div className="mt-8 pt-6 border-t-2 border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                    <button
                        className={`flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg ${
                            isAddedToCheckout
                            ? 'bg-green-600 text-white cursor-default'
                            : allEventsSatisfied && !hasTurnoConflict
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl hover:scale-105'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                        disabled={isAddedToCheckout || !allEventsSatisfied || hasTurnoConflict}
                        onClick={() => {
                            if (allEventsSatisfied) {
                                const selectionData = {
                                    tournamentClave: tournament.clave,
                                    jugadorClave: jugador.CLAVE,
                                    jugadorNombre: jugador.NOMBRECOMPLETO,
                                    jugadorTATID: jugador.FICHANAL,
                                    jugadorEMAIL: jugador.EMAIL,
                                    jugadorPAIS: jugador.CVEPAIS,
                                    paqueteClave: paquete.CLAVE,
                                    paqueteDescripcion: paquete.DESCRIPCION,
                                    costo: paquete.COSTO,
                                    cantidad: 1,
                                    divisionClave: division.CLAVE,
                                    divisionDescripcion: division.DESCRIPCION,
                                    items: descripcion.items.map(it => ({
                                        CVEITEM: it.CVEITEM,
                                        cantidad: it.CANTIDAD,
                                        item: it.ITEM,
                                    })),
                                    eventos: descripcion.eventos.map(ev => {
                                        const selectedTurnoIds = Array.from(selectedTurnos[ev.CVEEVENTO] || []);
                                        const waitlistTurnoIds = Array.from(waitlistTurnos);
                                        const turnosForEvent = allTurnos[ev.CVEEVENTO] || [];
                                        const turnosInfo = selectedTurnoIds.map(turnoId => {
                                            const turnoObj = turnosForEvent.find(t => t.CLAVE === turnoId);
                                            return turnoObj
                                                ? { TURNO: turnoObj.TURNO, HORARIO_I: turnoObj.HORARIO_I, DIASEMANA_I: turnoObj.DIASEMANA_I, CLAVE: turnoObj.CLAVE, alta: 1, waitlist: 0 }
                                                : { TURNO: turnoId, HORARIO_I: '', DIASEMANA_I: '', CLAVE: '', alta: 1, waitlist: 0 };
                                        });
                                        const waitlistInfo = waitlistTurnoIds
                                            .filter(turnoId => turnosForEvent.some(t => t.CLAVE === turnoId))
                                            .map(turnoId => {
                                                const turnoObj = turnosForEvent.find(t => t.CLAVE === turnoId);
                                                return turnoObj
                                                    ? { TURNO: turnoObj.TURNO, HORARIO_I: turnoObj.HORARIO_I, DIASEMANA_I: turnoObj.DIASEMANA_I, CLAVE: turnoObj.CLAVE, alta: 0, waitlist: 1 }
                                                    : { TURNO: turnoId, HORARIO_I: '', DIASEMANA_I: '', CLAVE: '', alta: 0, waitlist: 1 };
                                            });
                                        return {
                                            CVEEVENTO: ev.CVEEVENTO,
                                            evento: ev.EVENTO,
                                            turnos: selectedTurnoIds,
                                            turnosInfo: [...turnosInfo, ...waitlistInfo],
                                        };
                                    }),
                                };
                                onSelectionComplete(selectionData);
                                setIsAddedToCheckout(true);
                            }
                        }}
                    >
                        {isAddedToCheckout ? (
                            <>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Added to Checkout
                            </>
                        ) : (
                            <>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add to Cart
                            </>
                        )}
                    </button>

                    <button
                        className="flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl"
                        onClick={() => {
                            onRemove();
                            setIsAddedToCheckout(false);
                        }}
                        type="button"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                    </button>
                </div>
            </div>
            
            {hasTurnoConflict && (
                <div className="mt-4 flex items-start gap-3 bg-red-50 border-2 border-red-500 rounded-xl p-4">
                    <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                        <p className="text-red-900 font-bold text-base">Squad Conflict Detected</p>
                        <p className="text-red-700 text-sm mt-1">You've already registered for one of the selected squads. Please choose different squads to continue.</p>
                    </div>
                </div>
            )}
            {!allEventsSatisfied && (
                <div className="mt-4 flex items-start gap-3 bg-amber-50 border-2 border-amber-500 rounded-xl p-4">
                    <svg className="w-6 h-6 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                        <p className="text-amber-900 font-bold text-base">Complete Squad Selection</p>
                        <p className="text-amber-700 text-sm mt-1">Please select the required number of squads for each event before adding to cart.</p>
                    </div>
                </div>
            )}


        </div>
    );
};

export default TurnosSignUpSelection;